/**
 * Utility functions for comparing report data
 * This module provides reusable comparison logic that can be extended for different data types
 */

/**
 * Compare two numeric values and return a comparison object
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @param {string} unit - Unit of measurement (optional)
 * @returns {Object} Comparison result with trend, change, and percentage
 */
export const compareNumericValues = (current, previous, unit = "") => {
  if (previous === 0 || previous == null || current == null) {
    return {
      trend: "neutral",
      change: 0,
      percentage: 0,
      display: "No data",
      current,
      previous,
    };
  }

  const change = current - previous;
  const percentage = Math.round((change / previous) * 100);

  let trend = "neutral";
  if (Math.abs(percentage) >= 5) {
    // Only consider significant changes (5%+)
    trend = change > 0 ? "positive" : "negative";
  }

  const formatValue = (value) => {
    if (unit) {
      return `${value}${unit}`;
    }
    return value.toString();
  };

  return {
    trend,
    change,
    percentage,
    display: percentage > 0 ? `+${percentage}%` : `${percentage}%`,
    current: formatValue(current),
    previous: formatValue(previous),
    changeDisplay: change > 0 ? `+${change}${unit}` : `${change}${unit}`,
  };
};

/**
 * Compare completion rates between two datasets
 * @param {Object} currentData - Current completion data
 * @param {Object} previousData - Previous completion data
 * @returns {Object} Comparison result
 */
export const compareCompletionRates = (currentData, previousData) => {
  const currentRate = currentData.completed / currentData.total;
  const previousRate = previousData.completed / previousData.total;

  return {
    ...compareNumericValues(currentRate * 100, previousRate * 100, "%"),
    current: `${currentData.completed}/${currentData.total}`,
    previous: `${previousData.completed}/${previousData.total}`,
    currentRate,
    previousRate,
  };
};

/**
 * Compare exercise performance between two reports
 * @param {Array} currentExercises - Current report exercises
 * @param {Array} previousExercises - Previous report exercises
 * @returns {Object} Exercise comparison summary
 */
export const compareExercisePerformance = (
  currentExercises,
  previousExercises,
) => {
  if (!currentExercises || !previousExercises) {
    return { hasData: false };
  }

  // Calculate average weights
  const currentWeights = extractWeightsFromExercises(currentExercises);
  const previousWeights = extractWeightsFromExercises(previousExercises);

  const avgCurrentWeight =
    currentWeights.length > 0
      ? currentWeights.reduce((a, b) => a + b, 0) / currentWeights.length
      : 0;
  const avgPreviousWeight =
    previousWeights.length > 0
      ? previousWeights.reduce((a, b) => a + b, 0) / previousWeights.length
      : 0;

  // Calculate completion rates
  const currentCompletion = {
    completed: currentExercises.filter((ex) => ex.completed).length,
    total: currentExercises.length,
  };
  const previousCompletion = {
    completed: previousExercises.filter((ex) => ex.completed).length,
    total: previousExercises.length,
  };

  // Calculate set completion
  const currentSets = calculateSetCompletion(currentExercises);
  const previousSets = calculateSetCompletion(previousExercises);

  return {
    hasData: true,
    weightComparison: compareNumericValues(
      avgCurrentWeight,
      avgPreviousWeight,
      " lbs",
    ),
    exerciseCompletion: compareCompletionRates(
      currentCompletion,
      previousCompletion,
    ),
    setCompletion: compareCompletionRates(currentSets, previousSets),
    summary: generateExerciseSummary(currentExercises, previousExercises),
  };
};

/**
 * Extract weights from exercise data, handling both normal and super sets
 * @param {Array} exercises - Exercise array
 * @returns {Array} Array of weight values
 */
const extractWeightsFromExercises = (exercises) => {
  const weights = [];

  exercises.forEach((exercise) => {
    if (exercise.type === "super_set" && exercise.sets) {
      exercise.sets.forEach((set) => {
        if (set.exercises) {
          set.exercises.forEach((subExercise) => {
            if (subExercise.weight && subExercise.weight > 0) {
              weights.push(subExercise.weight);
            }
          });
        }
      });
    } else if (exercise.sets) {
      exercise.sets.forEach((set) => {
        if (set.weight && set.weight > 0) {
          weights.push(set.weight);
        }
      });
    }
  });

  return weights;
};

/**
 * Calculate set completion statistics
 * @param {Array} exercises - Exercise array
 * @returns {Object} Set completion data
 */
const calculateSetCompletion = (exercises) => {
  let totalSets = 0;
  let completedSets = 0;

  exercises.forEach((exercise) => {
    if (exercise.type === "super_set" && exercise.sets) {
      totalSets += exercise.sets.length;
      completedSets += exercise.sets.filter((set) => set.completed).length;
    } else if (exercise.sets) {
      totalSets += exercise.sets.length;
      completedSets += exercise.sets.filter((set) => set.completed).length;
    }
  });

  return { completed: completedSets, total: totalSets };
};

/**
 * Generate a human-readable summary of exercise comparison
 * @param {Array} currentExercises - Current exercises
 * @param {Array} previousExercises - Previous exercises
 * @returns {Array} Array of summary insights
 */
const generateExerciseSummary = (currentExercises, previousExercises) => {
  const insights = [];

  const currentWeights = extractWeightsFromExercises(currentExercises);
  const previousWeights = extractWeightsFromExercises(previousExercises);

  if (currentWeights.length > 0 && previousWeights.length > 0) {
    const avgCurrent =
      currentWeights.reduce((a, b) => a + b, 0) / currentWeights.length;
    const avgPrevious =
      previousWeights.reduce((a, b) => a + b, 0) / previousWeights.length;
    const change = avgCurrent - avgPrevious;

    if (Math.abs(change) >= 2) {
      // Significant weight change
      insights.push({
        type: change > 0 ? "positive" : "negative",
        message: `Average weight ${change > 0 ? "increased" : "decreased"} by ${Math.abs(change).toFixed(1)} lbs`,
      });
    }
  }

  const currentCompleted = currentExercises.filter((ex) => ex.completed).length;
  const previousCompleted = previousExercises.filter(
    (ex) => ex.completed,
  ).length;

  if (currentCompleted !== previousCompleted) {
    insights.push({
      type: currentCompleted > previousCompleted ? "positive" : "negative",
      message: `Completed ${currentCompleted} vs ${previousCompleted} exercises`,
    });
  }

  return insights;
};

/**
 * Format a comparison value for display
 * @param {Object} comparison - Comparison object from compareNumericValues
 * @returns {string} Formatted display string
 */
export const formatComparisonDisplay = (comparison) => {
  if (!comparison.hasData) {
    return "No data";
  }

  const sign = comparison.change > 0 ? "+" : "";
  return `${sign}${comparison.percentage}%`;
};

/**
 * Get trend color for Material-UI components
 * @param {string} trend - Trend value ('positive', 'negative', 'neutral')
 * @returns {string} Material-UI color name
 */
export const getTrendColor = (trend) => {
  switch (trend) {
    case "positive":
      return "success";
    case "negative":
      return "error";
    default:
      return "default";
  }
};

/**
 * Get trend icon name
 * @param {string} trend - Trend value
 * @returns {string} Icon component name
 */
export const getTrendIcon = (trend) => {
  switch (trend) {
    case "positive":
      return "TrendingUp";
    case "negative":
      return "TrendingDown";
    default:
      return "Remove";
  }
};
